import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button as ButtonV2 } from "../../Button";
import { MultiSelect, type MultiSelectOption } from "../../MultiSelect";
import { ResponsiveModal } from "../../ResponsiveModal";

type FilterModalProps = {
  open: boolean;
  subIbOptions: MultiSelectOption[];
  statusOptions: MultiSelectOption[];
  committedSubIb: string[];
  committedStatus: string[];
  onApply: (filters: { subIb: string[]; status: string[] }) => void;
  onClose: () => void;
};

export const FilterModal = ({
  open,
  subIbOptions,
  statusOptions,
  committedSubIb,
  committedStatus,
  onApply,
  onClose,
}: FilterModalProps) => {
  const { t } = useTranslation();
  const [draftSubIb, setDraftSubIb] = useState<MultiSelectOption[]>([]);
  const [draftStatus, setDraftStatus] = useState<MultiSelectOption[]>([]);

  // Sync the draft from the committed values whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setDraftSubIb(subIbOptions.filter((o) => committedSubIb.includes(o.value)));
    setDraftStatus(
      statusOptions.filter((o) => committedStatus.includes(o.value)),
    );
  }, [open, subIbOptions, statusOptions, committedSubIb, committedStatus]);

  const hasDraft = draftSubIb.length > 0 || draftStatus.length > 0;

  const handleApply = () => {
    onApply({
      subIb: draftSubIb.map((o) => o.value),
      status: draftStatus.map((o) => o.value),
    });
    onClose();
  };

  const handleClear = () => {
    setDraftSubIb([]);
    setDraftStatus([]);
  };

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={onClose}
      isMobile={false}
      title={t("subIBReport_filterModalTitle", "Filters")}
      contentOverflow="visible"
      footer={
        <div className="flex items-center justify-end gap-3">
          <ButtonV2
            variant="tonal"
            size="md"
            onClick={handleClear}
            disabled={!hasDraft}
          >
            {t("subIBReport_filterClear", "Clear")}
          </ButtonV2>
          <ButtonV2 variant="brand" size="md" onClick={handleApply}>
            {t("subIBReport_filterApply", "Apply")}
          </ButtonV2>
        </div>
      }
    >
      <MultiSelect
        label={t("subIBReport_filterSubIb", "Sub IB")}
        placeholder={t("subIBReport_filterSubIbPlaceholder", "All Sub IBs")}
        options={subIbOptions}
        selected={draftSubIb}
        onChange={setDraftSubIb}
      />
      <MultiSelect
        label={t("subIBReport_filterStatus", "Program status")}
        placeholder={t("subIBReport_filterStatusPlaceholder", "All statuses")}
        options={statusOptions}
        selected={draftStatus}
        onChange={setDraftStatus}
      />
    </ResponsiveModal>
  );
};
